import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SmsHubLayout } from "@/components/sms/SmsHubLayout";
import { listSuppression, addSuppression, removeSuppression, listAuditLog } from "@/server/sms.functions";
import { useAuth } from "@/hooks/useAuth";

export const Route = createFileRoute("/dashboard/admin/sms/compliance")({
  head: () => ({ meta: [{ title: "Compliance · SMS" }, { name: "robots", content: "noindex" }] }),
  component: Page,
});

function Page() {
  const { primaryRole } = useAuth();
  const qc = useQueryClient();
  const list = useServerFn(listSuppression);
  const add = useServerFn(addSuppression);
  const rem = useServerFn(removeSuppression);
  const audit = useServerFn(listAuditLog);

  const { data: supp, isLoading } = useQuery({ queryKey: ["sms-suppression"], queryFn: () => list(), enabled: primaryRole === "admin" });
  const { data: log } = useQuery({ queryKey: ["sms-audit"], queryFn: () => audit(), enabled: primaryRole === "admin" });

  const [phone, setPhone] = useState("");

  const addMut = useMutation({
    mutationFn: () => add({ data: { phone, reason: "manual" } }),
    onSuccess: () => { toast.success("Added to suppression"); setPhone(""); qc.invalidateQueries({ queryKey: ["sms-suppression"] }); },
    onError: (e: any) => toast.error(e.message ?? "Failed"),
  });
  const remMut = useMutation({
    mutationFn: (p: string) => rem({ data: { phone: p } }),
    onSuccess: () => { toast.success("Removed"); qc.invalidateQueries({ queryKey: ["sms-suppression"] }); },
  });

  return (
    <SmsHubLayout title="Compliance & Safety">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <h3 className="text-[13px] font-semibold mb-2">Suppression list</h3>
          <div className="flex gap-2 mb-3">
            <Input placeholder="+15551234567" value={phone} onChange={(e) => setPhone(e.target.value)} className="max-w-[220px]" />
            <Button size="sm" onClick={() => addMut.mutate()} disabled={!phone || addMut.isPending}><Plus className="h-3.5 w-3.5 mr-1.5" />Suppress</Button>
          </div>
          <div className="border border-border rounded-md">
            <Table>
              <TableHeader>
                <TableRow><TableHead>Phone</TableHead><TableHead>Reason</TableHead><TableHead>Added</TableHead><TableHead></TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && <TableRow><TableCell colSpan={4} className="text-center py-6"><Loader2 className="h-4 w-4 animate-spin inline" /></TableCell></TableRow>}
                {!isLoading && (supp ?? []).length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">Empty.</TableCell></TableRow>}
                {(supp ?? []).map((r: any) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-[12px]">{r.phone}</TableCell>
                    <TableCell className="text-[12px]">{r.reason}</TableCell>
                    <TableCell className="text-[11px] text-muted-foreground">{new Date(r.added_at).toLocaleString()}</TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => remMut.mutate(r.phone)}><Trash2 className="h-3.5 w-3.5" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
        <div>
          <h3 className="text-[13px] font-semibold mb-2">Admin audit log</h3>
          <div className="border border-border rounded-md max-h-[500px] overflow-auto">
            <Table>
              <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Action</TableHead><TableHead>Target</TableHead></TableRow></TableHeader>
              <TableBody>
                {(log ?? []).length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-6 text-muted-foreground">No actions yet.</TableCell></TableRow>}
                {(log ?? []).map((r: any) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-[11px] text-muted-foreground whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</TableCell>
                    <TableCell className="text-[12px] font-mono">{r.action}</TableCell>
                    <TableCell className="text-[11px] text-muted-foreground">{r.target_type ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      <div className="mt-6 border border-border rounded-md bg-card p-4">
        <h3 className="text-[13px] font-semibold mb-2">Safety controls</h3>
        <ul className="text-[12.5px] text-muted-foreground space-y-1.5 list-disc pl-5">
          <li>All campaigns default to <strong>Dry Run Mode</strong>. Live mode is globally disabled in Phase 1.</li>
          <li>Every template body is validated server-side: must end with exactly <code className="px-1 bg-muted rounded">Reply NO to opt out .</code></li>
          <li>Opted-out and suppressed numbers are excluded at queue generation and again at send time.</li>
          <li>Mon–Fri only, 7:00 AM – 7:00 PM, ~7 sends/min across 10 numbers (one every ~85–90s per number) with 10–25s random jitter.</li>
          <li>Daily cap default 5,000 — enforced before each tick.</li>
          <li>Admin role required for every SMS data action. Audit log captures all sensitive operations.</li>
        </ul>
      </div>
    </SmsHubLayout>
  );
}
